import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, ChevronDown, Plus, Rows3, SlidersHorizontal, ArrowDownUp, Trash2, RotateCcw, Check } from "lucide-react";
import { OptionList } from "@/components/tasks/TaskProps";
import { autoPill } from "@/components/shared/Pill";

// 377 (varianta B jako Airtable): Seskupit a řadit v jednom — nahoře skupiny, pod nimi řazení. Pole se vybírá
// ze seznamu s hledáním (jako Filtr), u obojího je směr. Bez podskupin a druhého řazení.
// fields = [{ value, label, icon, dirs: [první, druhý] }]; group = { k, dir } | null; order = { k, dir } (dir 0 = první popisek);
// onFold(true / false) = Sbalit vše / Rozbalit vše (na počítači v hlavičce, na telefonu pod skupinami).
// icons = ikony směrů (výchozí ↓ ↑); u řazení A → Z jde první ↑
function Seg({ dirs, icons = [ArrowDown, ArrowUp], value, onChange, testid, mobile }) {
  return (
    <div ref={autoPill} className="flex p-1 gap-0.5 bg-secondary rounded-[11px] min-w-0" role="radiogroup" data-testid={testid}>
      {dirs.map((l, i) => (
        <button key={l} type="button" role="radio" aria-checked={value === i} onClick={() => onChange(i)} data-testid={`${testid}-${i}`}
          className={`flex-1 min-w-0 ${mobile ? "h-10 text-[14.5px]" : "h-8 text-[13px]"} px-2.5 rounded-[8px] font-semibold inline-flex items-center justify-center gap-1.5 whitespace-nowrap
            ${value === i ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
          {(() => { const Ic = icons[i]; return <Ic size={14} className="shrink-0" />; })()}<span className="truncate">{l}</span>
        </button>
      ))}
    </div>
  );
}

function FieldRow({ fields, cur, open, onToggle, onPick, onDir, onClear, testid, mobile, placeholder }) {
  const f = fields.find((x) => x.value === cur.k) || fields[0];
  const Icon = f.icon;
  return (
    <div className="space-y-1.5">
      <div className={`grid gap-2 items-center ${mobile ? "grid-cols-[minmax(0,1fr)_auto]" : onClear ? "grid-cols-[170px_minmax(0,1fr)_32px]" : "grid-cols-[170px_minmax(0,1fr)_32px]"}`}>
        <button type="button" onClick={onToggle} aria-expanded={open} data-testid={testid}
          className={`${mobile ? "h-11 text-[15px]" : "h-9 text-sm"} min-w-0 rounded-[10px] border border-border bg-card px-2.5 inline-flex items-center gap-2 font-semibold hover:border-muted-foreground/50`}>
          {Icon && <Icon size={16} className="text-muted-foreground shrink-0" />}<span className="truncate">{f.label}</span><ChevronDown size={15} className="ml-auto opacity-50 shrink-0" />
        </button>
        {!mobile && <Seg dirs={f.dirs} icons={f.icons} value={cur.dir} onChange={onDir} testid={`${testid}-dir`} />}
        {onClear && (
          <button type="button" onClick={onClear} aria-label="Zrušit seskupení" title="Zrušit seskupení" data-testid={`${testid}-clear`}
            className={`${mobile ? "h-11 w-11" : "h-8 w-8"} rounded-[9px] grid place-items-center text-muted-foreground hover:text-destructive hover:bg-secondary`}><Trash2 size={16} /></button>
        )}
      </div>
      {mobile && <Seg dirs={f.dirs} icons={f.icons} value={cur.dir} onChange={onDir} testid={`${testid}-dir`} mobile />}
      {open && (
        <div className="rounded-[12px] border border-border bg-card p-1.5">
          <OptionList testid={`${testid}-list`} mobile={mobile} search placeholder={placeholder} onPick={(o) => onPick(o.value)}
            options={fields.map((x) => ({ value: x.value, label: x.label, on: x.value === cur.k, icon: x.icon ? <x.icon size={16} className="text-muted-foreground shrink-0" /> : null }))} />
        </div>
      )}
    </div>
  );
}

// 380: defText = popis výchozího zobrazení (vždy vidět dole), onReset = Obnovit výchozí (jen když se od něj liší).
// 389: extra = další volba stránky pod řazením (Doklady: Částky bez DPH / s DPH / obojí).
export function GroupSortPanel({ groupFields, group, onGroup, onGroupDir, orderFields, order, onOrder, onOrderDir, onFold, mobile, defText, onReset, extra = null }) {
  const [open, setOpen] = useState(null);   // "g" | "s" | "gnew"
  // zaměření do bubliny (bez rámečku na prvním tlačítku) — jinak první klik do bubliny zaměření „ztratí“ a bublina se zavře
  const rootRef = useRef(null);
  useEffect(() => { if (!mobile) rootRef.current?.focus({ preventScroll: true }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (k) => setOpen((o) => (o === k ? null : k));
  const lab = "flex items-center gap-1.5 text-xs font-semibold text-muted-foreground px-0.5";
  return (
    <div ref={rootRef} tabIndex={-1} className={`outline-none ${mobile ? "pb-2 space-y-2" : "p-3.5 space-y-2"}`} data-testid="group-sort-panel">
      {!mobile && (
        <div className="flex items-center gap-2 pb-1">
          <SlidersHorizontal size={16} /><span className="font-bold text-[15px]">Zobrazení</span>
          {group && onFold && (
            <span className="ml-auto flex gap-3 text-[12.5px] text-muted-foreground">
              <button type="button" onClick={() => onFold(true)} className="hover:text-foreground" data-testid="group-sort-fold">Sbalit vše</button>
              <button type="button" onClick={() => onFold(false)} className="hover:text-foreground" data-testid="group-sort-unfold">Rozbalit vše</button>
            </span>
          )}
        </div>
      )}
      <div className={lab}><Rows3 size={14} />Seskupit podle</div>
      {group ? (
        <FieldRow fields={groupFields} cur={group} open={open === "g"} onToggle={() => toggle("g")} mobile={mobile} testid="group-sort-group" placeholder="Seskupit podle…"
          onPick={(k) => { setOpen(null); onGroup(k); }} onDir={onGroupDir} onClear={() => { setOpen(null); onGroup(null); }} />
      ) : open === "gnew" ? (
        <div className="rounded-[12px] border border-border bg-card p-1.5">
          <OptionList testid="group-sort-group-list" mobile={mobile} search placeholder="Seskupit podle…" onPick={(o) => { setOpen(null); onGroup(o.value); }}
            options={groupFields.map((x) => ({ value: x.value, label: x.label, icon: x.icon ? <x.icon size={16} className="text-muted-foreground shrink-0" /> : null }))} />
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={() => toggle("gnew")} data-testid="group-sort-group-add"
            className={`inline-flex items-center gap-1.5 ${mobile ? "h-11 text-[15px]" : "h-9 text-sm"} px-1.5 rounded-[9px] font-semibold text-muted-foreground hover:text-foreground`}><Plus size={16} />Seskupit podle…</button>
          <span className="text-[12.5px] text-muted-foreground">teď bez skupin</span>
        </div>
      )}
      {group && onFold && mobile && (
        <div className="flex gap-2 pt-0.5">
          {[[true, "Sbalit vše", "group-sort-fold"], [false, "Rozbalit vše", "group-sort-unfold"]].map(([all, l, t]) => (
            <button key={t} type="button" onClick={() => onFold(all)} data-testid={t} className="h-10 px-4 rounded-full border border-border text-[14.5px] font-semibold">{l}</button>
          ))}
        </div>
      )}
      <div className={`${lab} pt-2`}><ArrowDownUp size={14} />Řadit podle</div>
      <FieldRow fields={orderFields} cur={order} open={open === "s"} onToggle={() => toggle("s")} mobile={mobile} testid="group-sort-order" placeholder="Řadit podle…"
        onPick={(k) => { setOpen(null); onOrder(k); }} onDir={onOrderDir} />
      {extra}
      {defText && (
        <div className={`flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-border ${mobile ? "pt-3 mt-1" : "pt-2.5 mt-1"}`} data-testid="group-sort-default">
          <span className={`min-w-0 flex-1 text-muted-foreground ${mobile ? "text-[14px]" : "text-[12.5px]"}`}>Výchozí: <b className="font-semibold text-foreground">{defText}</b></span>
          {onReset ? (
            <button type="button" onClick={onReset} data-testid="group-sort-reset"
              className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border font-semibold hover:bg-secondary ${mobile ? "h-10 px-4 text-[14.5px]" : "h-8 px-3 text-[13px]"}`}>
              <RotateCcw size={14} />Obnovit výchozí
            </button>
          ) : (
            <span className={`shrink-0 inline-flex items-center gap-1 font-semibold text-success ${mobile ? "text-[14px]" : "text-[12.5px]"}`} data-testid="group-sort-is-default"><Check size={14} />nastaveno</span>
          )}
        </div>
      )}
    </div>
  );
}
